import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listAssessments } from "@/lib/api";
import NavBar from "@/components/NavBar";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { Plus, ArrowRight } from "lucide-react";

function scoreColor(score) {
  if (score >= 75) return "#6CC24A";
  if (score >= 50) return "#EAB308";
  return "#EF4444";
}

export default function Assessments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const data = await listAssessments();
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <NavBar />
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="pp-label mb-2">Assessment History</div>
            <h1 className="font-display text-3xl md:text-4xl text-white leading-tight">
              Every business you've measured.
            </h1>
          </div>
          <button
            onClick={() => navigate("/new")}
            data-testid="history-new-assessment"
            className="pp-btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> New assessment
          </button>
        </div>

        {loading ? (
          <div className="pp-label" data-testid="history-loading">
            Loading history…
          </div>
        ) : items.length === 0 ? (
          <div className="pp-card p-10 text-center">
            <div className="pp-label mb-3">No assessments yet</div>
            <div className="font-display text-xl text-white mb-4">
              Run your first trust assessment to get started.
            </div>
            <button
              onClick={() => navigate("/new")}
              className="pp-btn-primary flex items-center gap-2 mx-auto"
              data-testid="history-empty-cta"
            >
              Run first assessment <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="pp-surface divide-y divide-[#333C47]">
            {items.map((a) => (
              <Link
                key={a.assessment_id}
                to={`/dashboard/${a.assessment_id}`}
                data-testid={`history-item-${a.assessment_id}`}
                className="flex items-center justify-between gap-4 p-5 hover:bg-[#232931] transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-display font-semibold text-white truncate">
                    {a.business_name}
                  </div>
                  <div className="text-xs text-[#C7CBD1] truncate">
                    {a.website} · {a.industry} ·{" "}
                    {new Date(a.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-6 shrink-0">
                  <div className="text-right">
                    <div className="pp-label text-[10px] mb-1">Confidence</div>
                    <ConfidenceBadge confidence={a.overall_confidence} />
                    {a.evidence_coverage?.coverage_pct != null && (
                      <div className="text-[10px] text-[#C7CBD1] mt-1">
                        {a.evidence_coverage.coverage_pct}% coverage
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="pp-label text-[10px]">Score</div>
                    <div
                      className="metric text-2xl font-bold"
                      style={{ color: scoreColor(a.overall_score) }}
                    >
                      {a.overall_score}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#C7CBD1]" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
